'use client';

import { useEffect, useState } from 'react';
import { MAX_GUESSES } from '../lib/types';

/* Guess-count distribution, mirroring SPOT's TodayDistribution / Connections'
   ConnectionsDistribution so a finished (or revisiting) player lands on the
   stats. The /decode/:date/stats endpoint buckets by (guesses, solved); we
   render solved-in-1..MAX_GUESSES plus a final "X" bucket for unsolved plays,
   highlighting the viewer's own bucket. */

interface Stats {
  total: number;
  avg: number | null;
  distribution: { guesses: number; solved: boolean; count: number }[];
}

export function DecodeDistribution({
  date,
  yourGuesses,
  solved,
}: {
  date: string;
  yourGuesses: number;
  solved: boolean;
}) {
  const [stats, setStats] = useState<Stats | null>(null);

  useEffect(() => {
    let alive = true;
    fetch(`/api/spot/decode/${date}/stats`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (alive) setStats(d as Stats);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [date]);

  if (!stats || stats.total === 0) return null;

  const solvedCount = (n: number) =>
    stats.distribution.find((b) => b.solved && b.guesses === n)?.count ?? 0;
  const failCount = stats.distribution
    .filter((b) => !b.solved)
    .reduce((s, b) => s + b.count, 0);

  const buckets: { label: string; count: number; mine: boolean }[] = [];
  for (let n = 1; n <= MAX_GUESSES; n++) {
    buckets.push({
      label: String(n),
      count: solvedCount(n),
      mine: solved && yourGuesses === n,
    });
  }
  buckets.push({ label: 'X', count: failCount, mine: !solved });

  const max = Math.max(...buckets.map((b) => b.count), 1);

  return (
    <div style={{ marginTop: 26, maxWidth: 420, marginInline: 'auto' }}>
      <div style={{ textAlign: 'center', fontWeight: 600, marginBottom: 12 }}>
        Guess distribution · {stats.total} played
        {stats.avg != null ? ` · avg ${stats.avg}` : ''}
      </div>
      {buckets.map((b) => {
        const pct = (b.count / max) * 100;
        return (
          <div
            key={b.label}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              margin: '5px 0',
            }}
          >
            <span
              style={{
                width: 16,
                textAlign: 'right',
                fontWeight: 700,
                fontSize: 13,
                opacity: b.mine ? 1 : 0.7,
              }}
            >
              {b.label}
            </span>
            <div
              style={{
                flex: 1,
                background: 'var(--border-soft)',
                borderRadius: 5,
                height: 24,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${b.count > 0 ? Math.max(pct, 8) : 0}%`,
                  height: '100%',
                  borderRadius: 5,
                  background: b.mine
                    ? 'var(--coral-deep)'
                    : 'var(--coral-soft)',
                  transition: 'width 0.5s cubic-bezier(0.2, 0.7, 0.2, 1)',
                }}
              />
              <span
                style={{
                  position: 'absolute',
                  right: 8,
                  top: 0,
                  lineHeight: '24px',
                  fontSize: 12,
                  fontWeight: b.mine ? 700 : 400,
                  color: 'var(--fg-1)',
                }}
              >
                {b.count}
                {b.mine ? ' · you' : ''}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
